import { AbstractService } from 'src/services/AbstractService';
import { config } from 'src/lib/config';
import { ServiceError } from 'src/lib/errors';
import qs from 'qs';

export type MatcherType = 'template' | 'logo';
export type DesignCreationType = 'Static' | 'Dynamic' | 'AutoLayout';

const DESIGN_CREATION_TYPES = ['Static', 'Dynamic', 'AutoLayout'];

interface BaseMatchingRequest {
    productKey: string;
    productVersion: string;
    culture: string;
    productOptions: Record<string, string>;
    designPersonalizationContextId?: string;
    previewHeight?: string;
    designCreationTypes?: string[];
    designCreationType?: DesignCreationType; // For single design type calls
}

interface TemplateMatchingRequest extends BaseMatchingRequest {
    preferredOptions?: Record<string, string>;
    templateIdentifier: string;
}

interface MatchingV2Response {
    previewUrl: string;
    fullProductSpecification: any;
    [key: string]: any;
}

class DesignConductorServiceApi extends AbstractService implements Services.DesignConductor.Content {
    public async getContent(
        productKey: string,
        productVersion: string,
        culture: string,
        designConceptId: string,
        selectedOptions?: Record<string, string>,
        signal?: AbortSignal,
    ): Promise<Models.DesignConductor.DesignContentResponse | null> {
        const url = `api/${this.version}/Galleries/dynamic__${productKey}__${productVersion}/Culture/${culture}/Content`;

        const queryParams: Record<string, string | string[]> = {
            designConceptIds: designConceptId,
            includeCategoryAndKeywordData: 'true',
            requestor: config.appName,
            limit: '1',
        };

        if (selectedOptions) {
            const attributes: string[] = [];

            Object.entries(selectedOptions).forEach(([key, value]) => {
                attributes.push(`${key.toLowerCase()}_${value.toLowerCase()}`);
            });
            if (attributes.length > 0) {
                queryParams.attributes = attributes;
            }
        }

        const searchParams = qs.stringify(queryParams, { arrayFormat: 'repeat' });

        try {
            const response = await this.api
                .get(
                    url,
                    {
                        searchParams,
                        signal,
                    },
                )
                .json<Models.DesignConductor.DesignContentResponse>();

            return response;
        } catch (e: any) {
            if (e.response?.status === 404) {
                return null;
            }

            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to fetch design content',
                info: (e as Error).message,
                url,
                productKey,
                productVersion,
                culture,
                designConceptId,
            });
        }
    }

    public async getTemplateMatching(
        request: TemplateMatchingRequest,
        signal?: AbortSignal,
    ): Promise<MatchingV2Response[]> {
        const url = `api/${this.version}/Product/${request.productKey}/Culture/${request.culture}/matching/template`;

        const queryParams: Record<string, string | string[] | Record<string, string>> = {
            productVersion: request.productVersion,
            templateIdentifier: request.templateIdentifier,
            previewHeight: request.previewHeight || '400',
            requestor: config.appName,
            // Use specific design type if provided, otherwise use all types
            designCreationTypes: request.designCreationType
                ? [request.designCreationType]
                : (request.designCreationTypes || DESIGN_CREATION_TYPES),
        };

        if (request.designPersonalizationContextId) {
            queryParams.designPersonalizationContextId = request.designPersonalizationContextId;
        }

        if (Object.keys(request.productOptions).length > 0) {
            queryParams.productOptions = request.productOptions;
        }

        if (request.preferredOptions && Object.keys(request.preferredOptions).length > 0) {
            queryParams.preferredOptions = request.preferredOptions;
        }

        const searchParams = qs.stringify(queryParams);

        try {
            const response = await this.api
                .get(url, {
                    searchParams,
                    signal,
                })
                .json<MatchingV2Response[]>();

            return response;
        } catch (e: any) {
            return DesignConductorServiceApi.handleServiceError(
                e,
                url,
                'Failed to call Template Matching',
                {
                    productKey: request.productKey,
                    productVersion: request.productVersion,
                    culture: request.culture,
                },
            );
        }
    }

    public async getLogoMatching(
        request: BaseMatchingRequest,
        signal?: AbortSignal,
    ): Promise<MatchingV2Response> {
        const url = `api/${this.version}/Product/${request.productKey}/Culture/${request.culture}/matching/logo`;

        const queryParams: Record<string, string | string[] | Record<string, string>> = {
            productVersion: request.productVersion,
            previewHeight: request.previewHeight || '400',
            requestor: config.appName,
            designCreationTypes: DESIGN_CREATION_TYPES,
        };

        if (request.designPersonalizationContextId) {
            queryParams.designPersonalizationContextId = request.designPersonalizationContextId;
        }

        if (Object.keys(request.productOptions).length > 0) {
            queryParams.productOptions = request.productOptions;
        }

        const searchParams = qs.stringify(queryParams);

        try {
            const response = await this.api
                .get(url, {
                    searchParams,
                    signal,
                })
                .json<MatchingV2Response>();

            return response;
        } catch (e: any) {
            return DesignConductorServiceApi.handleServiceError(
                e,
                url,
                'Failed to call Logo Matching',
                {
                    productKey: request.productKey,
                    productVersion: request.productVersion,
                    culture: request.culture,
                },
            );
        }
    }

    private static async handleServiceError(
        error: any,
        url: string,
        fallbackMessage: string,
        context: { productKey?: string; productVersion?: string; culture?: string; designConceptId?: string },
    ): Promise<never> {
        let errorMessage = fallbackMessage;

        if (error.response?.status === 404) {
            try {
                const responseBody = await error.response.json();

                errorMessage = responseBody?.title || fallbackMessage;
            } catch {
                // If parsing fails, use the fallback message
                errorMessage = fallbackMessage;
            }
        }

        throw new ServiceError({
            ...(error as Error),
            message: errorMessage,
            info: (error as Error).message,
            url,
            ...context,
        });
    }
}

export const DesignConductorService = new DesignConductorServiceApi(config.services.designConductorServiceApi);
